import moment from 'moment';
import React, { Fragment } from 'react';
import { Fund } from 'src/components/Exchange/types';
import If from 'src/components/Shared/If/If';
import Phone from 'src/components/Shared/Phone/Phone';
import config from 'src/config/config';
import {
  APP_TOLL_FREE_NUMBER,
  FundNoteExchange,
  TOLL_FREE_NUMBER,
} from 'src/settings/settings';
import AddressUndeliverableFundNoteTip from './AddressUndeliverableFundNoteTip';
import ESAPlanFundNoteTip from './ESAPlanFundNoteTip';
import FundNote from './FundNote';
import './FundNote.module.scss';
import PendingTransactionFundNoteTip from './PendingTransactionFundNoteTip';
import RecentlyRedeemedFundNoteTip from './RecentlyRedeemedFundNoteTip';
import RestrictedCountryFundNoteTip from './RestrictedCountryFundNoteTip';
import styles from '../../../Shared/APPText/APPText.module.scss';

const ExchangeFundNotes: React.FC<{ fund: Fund }> = ({ fund }) => (
  <Fragment>
    <If test={fund.notes[FundNoteExchange.EXCHANGE_UNAVAILABLE]}>
      <FundNote
        labelSubText=""
        labelText="Exchange Unavailable"
        tipHeader="Exchange Unavailable"
        tipMessage="The exchange feature is currently not available for this account."
      />
    </If>
    <If test={fund.notes[FundNoteExchange.INELIGIBLE_BALANCE]}>
      <FundNote
        labelSubText=""
        labelText="Ineligible Balance"
        tipHeader="Ineligible Balance"
        tipMessage={
          <div className="exclude">
            ActivePlus Portfolios<sup className={styles.sup}>®</sup> accounts
            require a minimum balance of $10,000 to stay in the service. If you
            would like to proceed with closing out your account, please select
            "Exchange all investments & close account"
            <br />
            <br />
            For more information, please call{' '}
            <Phone phoneNumber={APP_TOLL_FREE_NUMBER} />.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.NOT_ELIGIBLE]}>
      <FundNote
        labelSubText={''}
        labelText="Ineligible Exchange Account"
        tipHeader="Ineligible Exchange Account"
        tipMessage={
          <div className="exclude">
            Exchanges among your selected investment(s) and this investment are
            ineligible due to differences in the account details.{' '}
            <a
              className="exclude no-break"
              href={`${config.url}/personal-investing/help/transactions.html`}
            >
              Learn why.
            </a>
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.BUCKET_ROLL]}>
      <FundNote
        labelSubText={''}
        labelText="IRA Contributions Unavailable"
        tipHeader="IRA Contributions Unavailable"
        tipMessage={
          <>
            Sorry, contributions to your IRA(s) are temporarily unavailable due
            to year-end maintenance. Please check back after 7 PM EDT on January
            1st to make a contribution. If you are attempting to make a purchase
            into Government Money Fund or US Treasury Money Fund prior to 5pm on
            the last business day of the year, please{' '}
            <a href="https://www.troweprice.com/personal-investing/help/contact/index.html">
              contact us
            </a>
            .
          </>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.UNAVAILABLE]}>
      <FundNote
        labelSubText={''}
        labelText="Exchange Restricted"
        tipHeader="Exchange Restricted"
        tipMessage={
          <div className="exclude">
            This fund is not set up to exchange online. Please call an associate
            at {TOLL_FREE_NUMBER} to update the appropriate settings.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.FROM_ZERO]}>
      <FundNote
        labelSubText=""
        labelText="Exchange Unavailable"
        tipHeader="Exchange Unavailable"
        tipMessage="You are unable to exchange out of this account due to zero shares. An exchange from another account into this account is still allowed."
      />
    </If>
    <If test={fund.notes[FundNoteExchange.FIDUCIARY_TYPE]}>
      <FundNote
        labelSubText="Clients are not able to exchange between fiduciary and non-fiduciary accounts."
        labelText="Unavailable for Exchange"
        showToolTip={false}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.COUNTRY_RESTRICTED]}>
      <FundNote
        labelSubText="Requires Permanent US Address."
        labelText="Restricted"
        tipHeader="Restricted Country"
        tipMessage={<RestrictedCountryFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.STOP_TRANSFER]}>
      <FundNote
        labelSubText=""
        labelText="Stop Transfer"
        tipHeader="Stop Transfer"
        tipMessage="Your account has a feature on it that restricts exchanges."
      />
    </If>
    <If
      test={
        fund.notes[FundNoteExchange.WITHIN_CURRENT_INVESTMENTS] ||
        fund.notes[FundNoteExchange.ACH_DIVIDEND]
      }
    >
      <FundNote
        labelSubText=""
        labelText="Exchange Within Current Investments"
        tipHeader="Exchange Within Current Investments"
        tipMessage={
          <div className="exclude">
            This investment can only be exchanged within existing investments in
            your portfolio because of its dividend settings.
            <a
              className="exclude"
              href="https://www.troweprice.com/personal-investing/help/contact/index.html"
              rel="noreferrer"
              target="_blank"
            >
              Contact us
            </a>{' '}
            to update your settings.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.REQUIRES_CONVERSION]}>
      <FundNote
        labelSubText=""
        labelText="Requires Conversion"
        tipHeader="Requires Conversion"
        tipMessage={
          <div className="exclude">
            To convert your eligible IRA to a Roth IRA, use our{' '}
            <a
              className="exclude no-break"
              href={`${config.url}/usis/transactions/roth-conversion`}
            >
              Roth IRA Conversion Tool.
            </a>
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.ESA_PLAN]}>
      <FundNote
        labelSubText=""
        labelText="ESA Plan"
        tipHeader="ESA Plan"
        tipMessage={<ESAPlanFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.RECENTLY_REDEEMED]}>
      <FundNote
        labelSubText={`Available on or after ${moment(
          fund.purchaseBlockRemovalDate
        ).format('MM/DD')}.`}
        labelText="Recently Redeemed"
        tipHeader="Recently Redeemed"
        tipMessage={<RecentlyRedeemedFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.ADDRESS_UNDELIVERABLE]}>
      <FundNote
        labelSubText=""
        labelText="Address Undeliverable"
        tipHeader="Address Undeliverable"
        tipMessage={<AddressUndeliverableFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.PENDING]}>
      <FundNote
        labelSubText=""
        labelText="Transaction Pending"
        tipHeader="Transaction Pending"
        tipMessage={<PendingTransactionFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.PENDING_TRANSACTION]}>
      <FundNote
        labelSubText=""
        labelText="Transaction Pending"
        tipHeader="Transaction Pending"
        tipMessage={<PendingTransactionFundNoteTip allFunds />}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.MONEY_MARKET_EVENT_GATE_ACTIVE]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Redemption Block"
        showToolTip={false}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.MONEY_MARKET_EVENT_GATE_REMOVED]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Redemption Block Removed"
        showToolTip={false}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.MONEY_MARKET_EVENT_FEE_ACTIVE]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Liquidity Fee"
        showToolTip={false}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.MONEY_MARKET_EVENT_FEE_REMOVED]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Liquidity Fee Removed"
        showToolTip={false}
      />
    </If>
    <If test={fund.notes[FundNoteExchange.UNAVAILABLE_FOR_REDEMPTION_ONLINE]}>
      <FundNote
        labelSubText=""
        labelText="Unavailable Online"
        tipHeader="Unavailable Online"
        tipMessage={
          <div className="exclude">
            Online/phone redemption disabled. Call{' '}
            <Phone phoneNumber={TOLL_FREE_NUMBER} /> to enable redemptions.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.CLOSED_NEW]}>
      <FundNote
        labelText="Closed to New Investors"
        tipHeader="Closed to New Investors"
        tipMessage="All shares previously redeemed, client cannot buy back into fund."
      />
    </If>
    <If test={fund.notes[FundNoteExchange.PENALTY_WITHHOLDING]}>
      <FundNote
        labelSubText=""
        labelText="Exchange Unavailable"
        tipHeader="Exchange Unavailable"
        tipMessage="Restrictive feature regarding tax withholding on your account does not allow exchanges online."
      />
    </If>
    <If test={fund.notes[FundNoteExchange.RETURN_MAIL_VERIFY_ADDRESS]}>
      <FundNote
        labelSubText=""
        labelText="Exchange Unavailable"
        tipHeader="Exchange Unavailable"
        tipMessage="We have received undeliverable mail from the Post Office for your address on record. Please verify your address on record is correct and update if necessary."
      />
    </If>
    <If test={fund.notes[FundNoteExchange.AUTOMATED_SYSTEMS_STOP_BALANCE]}>
      <FundNote
        labelSubText=""
        labelText="Exchange Unavailable"
        tipHeader="Exchange Unavailable"
        tipMessage="You are unable to exchange out of this account due to zero shares. An exchange from another account into this account is still allowed."
      />
    </If>
    <If
      test={
        (fund.notes[FundNoteExchange.STOP_PURCHASE] ||
          fund.notes[FundNoteExchange.AUTOMATED_SYSTEMS_STOP_ALL]) &&
        fund.productType === 'ManagedAccount'
      }
    >
      <FundNote
        labelSubText=""
        labelText="Exchange Unavailable"
        tipHeader="Exchange Unavailable"
        tipMessage={
          <div className="exclude">
            Your account has a feature on it that restricts exchanges. Call{' '}
            <Phone phoneNumber={APP_TOLL_FREE_NUMBER} /> to discuss options
          </div>
        }
      />
    </If>
    <If
      test={
        fund.notes[FundNoteExchange.AUTOMATED_SYSTEMS_STOP_CHECK] ||
        fund.notes[FundNoteExchange.AUTOMATED_SYSTEMS_STOP_PURCHASE] ||
        fund.notes[FundNoteExchange.TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE] ||
        fund.notes[FundNoteExchange.FROM_AUTOMATED_SYSTEMS_STOP_EXCHANGE]
      }
    >
      <FundNote
        labelSubText=""
        labelText="Exchange Unavailable"
        tipHeader="Exchange Unavailable"
        tipMessage={
          <div className="exclude">
            Your account has a feature on it that restricts exchanges. Call{' '}
            {fund.productType === 'ManagedAccount' ? (
              <Phone phoneNumber={APP_TOLL_FREE_NUMBER} />
            ) : (
              <Phone phoneNumber={TOLL_FREE_NUMBER} />
            )}{' '}
            to discuss options.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteExchange.EXISTING_ICLASS]}>
      <FundNote
        labelSubText=""
        labelText="I Class"
        tipHeader="I Class Share Pricing"
        tipMessage={
          <div>
            I Class shares have a lower expense ratio than Investor Class.
          </div>
        }
      />
    </If>
  </Fragment>
);

export default ExchangeFundNotes;
